import React, { useState, useEffect } from 'react';
import { ChevronRight, Database, Cloud, Package, Check, AlertCircle, Loader2 } from 'lucide-react';

interface Step {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  data?: any;
  timestamp?: string;
  duration?: number;
}

interface StepByStepWrapperProps {
  steps: Step[];
  onStepUpdate: (stepId: string, update: Partial<Step>) => void;
}

export const StepByStepWrapper: React.FC<StepByStepWrapperProps> = ({ steps, onStepUpdate }) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const getStatusIcon = (status: Step['status']) => {
    switch (status) {
      case 'completed':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'active':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const getStepColor = (status: Step['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'active':
        return 'bg-blue-50 border-blue-200 animate-pulse';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Package className="w-5 h-5" />
        Data Flow Visualization
      </h3>

      <div className="space-y-3">
        {steps.map((step, index) => (
          <div key={step.id}>
            <div
              className={`border rounded-lg p-4 transition-all ${getStepColor(step.status)}`}
              onClick={() => toggleStep(step.id)}
            >
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-3">
                  {getStatusIcon(step.status)}
                  <span className="font-medium text-gray-900">{step.label}</span>
                  {step.timestamp && (
                    <span className="text-xs text-gray-500">
                      {new Date(step.timestamp).toLocaleTimeString()}
                    </span>
                  )}
                  {step.duration && (
                    <span className="text-xs text-gray-500">
                      ({step.duration}ms)
                    </span>
                  )}
                </div>
                <ChevronRight
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedSteps.has(step.id) ? 'rotate-90' : ''
                  }`}
                />
              </div>

              {expandedSteps.has(step.id) && step.data && (
                <div className="mt-3 pt-3 border-t">
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                    {JSON.stringify(step.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {index < steps.length - 1 && (
              <div className="flex justify-center py-1">
                <div className="w-0.5 h-6 bg-gray-300" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {steps.filter(s => s.status === 'completed').length}
            </div>
            <div className="text-gray-500">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {steps.filter(s => s.status === 'active').length}
            </div>
            <div className="text-gray-500">Active</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-400">
              {steps.filter(s => s.status === 'pending').length}
            </div>
            <div className="text-gray-500">Pending</div>
          </div>
        </div>
      </div>
    </div>
  );
};